import {
  Accordion,
  AccordionContent,
  AccordionTrigger
} from '@deque/cauldron-react';
import React, {
  type ReactElement,
  useEffect,
  useLayoutEffect,
  useMemo
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ContentToast from '~/common/components/ContentToast';
import CommonLink from '~/common/components/Link';
import PageTitle from '~/common/components/PageTitle';
import { PRODUCT_URLS, ProductSlugs } from '~/common/constants';
import { useAxeDevtoolsProAnalytics } from '~/common/contexts/analytics';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import sendPlanChange from '~/common/utils/send-plan-change-to-extension';
import InstallExtension from '../containers/InstallExtension';
import useFAQs from '../hooks/useFAQs';
import styles from './Home.css';

const Home = (): ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();
  const hasProWalkthrough = useFeatureFlagState('pro_walkthrough_v1');
  const {
    purchaseSuccess,
    invitationAccepted,
    alreadyAcceptedInvite,
    invitationRejected
  } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      purchaseSuccess: params.get('purchase-success') === 'true',
      invitationAccepted: params.get('invitation-accepted') === 'true',
      invitationRejected: params.get('invitation-rejected') === 'true',
      alreadyAcceptedInvite: params.get('already-accepted-invite') === 'true'
    };
  }, [location]);
  const faqs = useFAQs();
  const analytics = useAxeDevtoolsProAnalytics();

  useEffect(() => {
    analytics.viewWelcomePage(window.location.href);
  }, []);

  useLayoutEffect(() => {
    // Add the Welcome class to the body so we can "cancel" out the max-widths.
    // This makes it possible for the gradient background to stretch full-width
    document.body.classList.add('Welcome');
    return () => document.body.classList.remove('Welcome');
  }, []);

  useEffect(() => {
    if (purchaseSuccess) {
      // notify the extension that the user's plan has changed
      sendPlanChange();
    }
  }, []);

  return (
    <div className={styles.wrap}>
      <PageTitle title={t('Welcome')} />
      {purchaseSuccess && (
        <ContentToast show type="confirmation">
          {t('Your payment was successful!')}
        </ContentToast>
      )}
      {invitationAccepted && (
        <ContentToast show type="confirmation">
          {t('You have accepted your invitation!')}
        </ContentToast>
      )}
      {invitationRejected && (
        <ContentToast show type="caution">
          {t('You have declined your invitation!')}
        </ContentToast>
      )}
      {alreadyAcceptedInvite && (
        <ContentToast show type="caution">
          {t('You have already accepted your invitation!')}
        </ContentToast>
      )}
      <section className={styles.topSection}>
        <h1>{t('Welcome to Axe DevTools Extension')}</h1>
        <Trans>
          <p>
            Join the{' '}
            <a href="https://accessibility.deque.com/thank-you-axe-community">
              Axe Slack Community
            </a>{' '}
            and get the most out of your Axe products. Ask questions and provide
            feedback to the Deque team members. Connect with other Axe users.
          </p>
        </Trans>
        <InstallExtension />
        <Trans>
          <p className={styles.videoInfo}>
            To get more familiar with the Axe DevTools Browser Extension, watch
            the video{' '}
            <CommonLink url="https://www.youtube.com/watch?v=iRGB40c_YJc">
              Getting Started with the Axe DevTools Browser Extension video
            </CommonLink>
          </p>
        </Trans>
      </section>
      <section className={styles.faqSection}>
        <div className={styles.faq}>
          {hasProWalkthrough && (
            <div className={styles.walkthroughIntro}>
              <h2>{t('Ready to get started?')}</h2>
              <p>
                {t(
                  'Follow along with our step by step walkthrough of the extension. We will show you how the product works so you can start fixing accessibility issues.'
                )}
              </p>
              <CommonLink
                url={`${
                  PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
                }/walkthrough?utm_source=webapp_welcome&utm_medium=referral&utm_campaign=start_walkthrough`}
                variant="button"
              >
                {t('Start walkthrough')}
              </CommonLink>
            </div>
          )}
          <h2>{t('Frequently Asked Questions')}</h2>
          <div className={styles.hrDot} />
          <div className={styles.faqAccordions}>
            {faqs.map(({ answer, question }, index) => (
              <Accordion key={index}>
                <AccordionTrigger>{question}</AccordionTrigger>
                <AccordionContent className={styles.faqAnswer}>
                  {answer}
                </AccordionContent>
              </Accordion>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
